import React from "react";
import { Link } from "zudoku/components";
import { brand } from "../../brands/index.js";
import { t } from "../../i18n/index.js";

const isMinimal = brand.theme.landingStyle === "minimal";

type Endpoint = {
  method: "GET" | "POST";
  path: string;
  desc: string;
  href: string;
};

function getEndpoints(): Endpoint[] {
  const s = t.endpointShowcase;
  const links = brand.landingLinks;
  return [
    {
      method: "GET",
      path: "/open/v1/products/search",
      desc: s.productsSearchDesc,
      href: links.ecommerceProducts,
    },
    {
      method: "GET",
      path: "/open/v1/creators/search",
      desc: s.creatorsSearchDesc,
      href: links.ecommerceCreators,
    },
    {
      method: "POST",
      path: "/open/v1/ai/sd-pro/video/generation",
      desc: s.sdProVideoDesc,
      href: links.aiVideo,
    },
    {
      method: "POST",
      path: "/open/v1/social/accounts/list",
      desc: s.socialAccountsDesc,
      href: links.social,
    },
  ];
}

const methodStyles: Record<Endpoint["method"], React.CSSProperties> = {
  GET: {
    color: "#0e7a3d",
    background:
      "color-mix(in oklab, oklch(0.65 0.18 145) 15%, transparent)",
  },
  POST: {
    color: "var(--brand-primary)",
    background:
      "color-mix(in oklab, var(--brand-primary) 15%, transparent)",
  },
};

export function EndpointShowcase() {
  const s = t.endpointShowcase;
  const endpoints = getEndpoints();

  return (
    <section style={{ margin: isMinimal ? "96px 0" : "48px 0" }}>
      <header style={{ marginBottom: isMinimal ? "28px" : "16px", textAlign: isMinimal ? "center" : "left" }}>
        <div
          style={{
            fontSize: "12px",
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: isMinimal ? "var(--muted-foreground)" : "var(--brand-primary)",
            marginBottom: "8px",
          }}
        >
          {s.eyebrow}
        </div>
        <h2
          style={{
            margin: 0,
            fontSize: isMinimal ? "clamp(28px, 4vw, 40px)" : "26px",
            fontWeight: 600,
            letterSpacing: "-0.02em",
          }}
        >
          {s.heading}
        </h2>
      </header>

      <div
        style={{
          border: "1px solid var(--border)",
          borderRadius: "var(--radius)",
          overflow: "hidden",
          background: "var(--card)",
        }}
      >
        {endpoints.map((ep, i) => (
          <Link
            key={ep.path}
            to={ep.href}
            style={{
              display: "grid",
              gridTemplateColumns: "64px minmax(0, 1.4fr) minmax(0, 2fr) auto",
              alignItems: "center",
              gap: "12px",
              padding: "14px 18px",
              textDecoration: "none",
              color: "inherit",
              borderTop: i === 0 ? "none" : "1px solid var(--border)",
              transition: "background 120ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = isMinimal
                ? "color-mix(in oklab, var(--muted) 70%, transparent)"
                : "var(--brand-gradient-soft)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "";
            }}
          >
            <span
              style={{
                justifySelf: "start",
                padding: "3px 8px",
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.05em",
                borderRadius: "4px",
                fontFamily: "var(--font-mono)",
                ...methodStyles[ep.method],
              }}
            >
              {ep.method}
            </span>
            <code
              style={{
                fontSize: "13px",
                fontFamily: "var(--font-mono)",
                color: "var(--foreground)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {ep.path}
            </code>
            <span
              style={{
                fontSize: "13px",
                color: "var(--muted-foreground)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {ep.desc}
            </span>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: "var(--muted-foreground)" }}
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </Link>
        ))}
      </div>
    </section>
  );
}
